/**
 * ConsoleLine - Single line in a retro terminal console
 *
 * Supports optional typewriter effect and blinking cursor.
 * Accent color is configurable per-app (gold for EMS, purple for PhD).
 */

import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxTypography } from '../core/theme';

export default function ConsoleLine({
    prefix,
    text,
    color,
    active = false,
    typewriter = false,
    accentColor = '#b28c50',
}) {
    const [displayText, setDisplayText] = useState(typewriter ? '' : text);
    const textRef = useRef(text);

    useEffect(() => {
        if (!typewriter) {
            setDisplayText(text);
            return;
        }
        textRef.current = text;
        setDisplayText('');
        let i = 0;
        const timer = setInterval(() => {
            i++;
            if (i <= text.length && textRef.current === text) {
                setDisplayText(text.slice(0, i));
            } else {
                clearInterval(timer);
            }
        }, 12);
        return () => clearInterval(timer);
    }, [text, typewriter]);

    return (
        <Box sx={{ display: 'flex', gap: 1, py: 0.2 }}>
            <Typography
                component="span"
                sx={{
                    color: accentColor,
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    lineHeight: 1.7,
                    opacity: 0.7,
                    minWidth: 36,
                    flexShrink: 0,
                }}
            >
                [{prefix}]
            </Typography>
            <Typography
                component="span"
                sx={{
                    color: color || 'rgba(244,241,236,0.6)',
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    lineHeight: 1.7,
                    opacity: active ? 1 : 0.8,
                }}
            >
                {displayText}
                {active && (
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-block',
                            width: 6,
                            height: 12,
                            background: accentColor,
                            ml: 0.5,
                            verticalAlign: 'text-bottom',
                            '@keyframes cursorBlink': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0 },
                            },
                            animation: 'cursorBlink 1s step-end infinite',
                        }}
                    />
                )}
            </Typography>
        </Box>
    );
}
